import React, { useEffect } from 'react';
import { Card } from '@material-ui/core';
import { History as HistoryIcon } from '@material-ui/icons';

import CardTitle from '../../../common/card/CardTitle';
import CardContent from '../../../common/card/CardContent';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineTextField from '../../../common/inline/InlineTextField';
import InlineRadioGroup, {
  InlineRadioGroupProps
} from '../../../common/inline/InlineRadioGroup';
// import InlineDatePicker from '../../../common/inline/InlineDatePicker';
import { WorkflowEntity } from '../../workflow/Workflow.interface';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { useUpdateSupplier } from '../Supplier.hooks';
import { BaseSupplierCardProps } from '../Supplier.interface';

const SupplierQualitySystemCard: React.FC<BaseSupplierCardProps> = (
  props: BaseSupplierCardProps
) => {
  const {
    supplier,
    supplierVersion,
    token,
    onChange,
    onUpdate,
    onError
  } = props;
  const {
    updatedSupplier,
    isUpdating,
    updateError,
    updateSupplier
  } = useUpdateSupplier(supplier.id, token);
  const { isFieldDisabled } = useTaskEntityFields<WorkflowEntity.Supplier>(
    supplier.task,
    { token }
  );
  const disabled = !!supplierVersion;
  const initialData = supplierVersion || supplier;
  const commonOptions: InlineRadioGroupProps['options'] = [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
    { label: 'N/A', value: 'n/a' }
  ];
  const commonGridProps: InlineRadioGroupProps['grid'] = {
    xs: 12
  };

  useEffect(() => {
    if (updatedSupplier) {
      onChange?.(updatedSupplier);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedSupplier]);

  useEffect(() => {
    if (updateError) {
      onError?.(updateError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateError]);

  return (
    <Card>
      <CardTitle>
        <HistoryIcon />
        Quality System Functions
      </CardTitle>
      <CardContent padding='large'>
        <InlineFormik
          isSubmitting={isUpdating}
          initialValues={{
            hasInternalAuditProgram: initialData.hasInternalAuditProgram || '',
            noInternalAuditProgramExplain:
              initialData.noInternalAuditProgramExplain || '',
            internalAuditSchedule: initialData.internalAuditSchedule || '',
            hasCustomerComplaintProgram:
              initialData.hasCustomerComplaintProgram || '',
            hasRootCauseAnalysisProgram:
              initialData.hasRootCauseAnalysisProgram || '',
            hasCorrectiveActionProgram:
              initialData.hasCorrectiveActionProgram || '',
            hasCorrectiveActionProgramRetrospective:
              initialData.hasCorrectiveActionProgramRetrospective || ''
          }}
          onSubmit={(supplierDto: Partial<any>) => {
            updateSupplier(supplierDto);
            onChange?.(supplierDto);
          }}
        >
          {() => (
            <InlineForm>
              <InlineRadioGroup
                name='hasInternalAuditProgram'
                label='Is there an internal audit function, with audits and corrective actions maintained on file?'
                grid={commonGridProps}
                options={commonOptions}
                disabled={
                  disabled || isFieldDisabled('hasInternalAuditProgram')
                }
              />
              <InlineTextField
                multiline
                name='noInternalAuditProgramExplain'
                label='If you answered NO or N/A to the question above, please explain:'
                placeholder='Enter explanation'
                disabled={
                  disabled || isFieldDisabled('noInternalAuditProgramExplain')
                }
              />
              <InlineTextField
                multiline
                name='internalAuditSchedule'
                label='How often are internal audits conducted?'
                placeholder='Enter answer'
                disabled={disabled || isFieldDisabled('internalAuditSchedule')}
              />
              <InlineRadioGroup
                name='hasCustomerComplaintProgram'
                label='Is there a mechanism for handling customer complaints?'
                grid={commonGridProps}
                options={commonOptions}
                disabled={
                  disabled || isFieldDisabled('hasCustomerComplaintProgram')
                }
              />
              <InlineRadioGroup
                name='hasRootCauseAnalysisProgram'
                label='Is analysis performed to identify root causes of problems/failures?'
                grid={commonGridProps}
                options={commonOptions}
                disabled={
                  disabled || isFieldDisabled('hasRootCauseAnalysisProgram')
                }
              />
              <InlineRadioGroup
                name='hasCorrectiveActionProgram'
                label='Is there a Corrective/Preventive Action program in place?'
                grid={commonGridProps}
                options={commonOptions}
                disabled={
                  disabled || isFieldDisabled('hasCorrectiveActionProgram')
                }
              />
              <InlineRadioGroup
                name='hasCorrectiveActionProgramRetrospective'
                label='Is there a process for actions to be taken when timely or effective corrective actions are not achieved?'
                grid={commonGridProps}
                options={commonOptions}
                disabled={
                  disabled ||
                  isFieldDisabled('hasCorrectiveActionProgramRetrospective')
                }
              />
              {/* <InlineDatePicker
                name='completionDate'
                placeholder='mm/dd/yyyy'
                label='Evaluation Completion Date'
              /> */}
            </InlineForm>
          )}
        </InlineFormik>
      </CardContent>
    </Card>
  );
};

export default SupplierQualitySystemCard;
